import React, { useMemo } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Box from '@mui/material/Box';
import { visuallyHidden } from '@mui/utils';
// import { ChatResponseAnswer } from '../../pages/ChatPage'
import TableHeader from './TableHeader';
import ParetoChart from './ParetoChart';

interface Column {
    id: string;
    label: string;
    minWidth?: number;
    align?: 'right';
    format?: (value: any) => string;
    numeric?: boolean;
}

interface TabularDataProps {
    data: any
}

const isFloat = (num: any) => {
    return typeof num === "number" && !Number.isInteger(num);
}

type Order = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const TabularData: React.FC<TabularDataProps> = ({ data: dataProps }) => {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [axes, setAxes] = React.useState({ x: '', y: '' })
    const [displayType, setDisplayType] = React.useState<'table' | 'chart'>('table');
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<string>('');
    
    const columnNames = Object.keys(dataProps[0])

    let valueHeading = ''
    valueHeading = valueHeading.replace('_', ' ')
    valueHeading = valueHeading.charAt(0).toUpperCase() + valueHeading.slice(1)

    ///////////////////////////////////////////////////////////////////////////
    ///////////////////////////////////////////////////////////////////////////
    const columns: readonly Column[] = Object.keys(dataProps[0]).map(r => ({ 
        id: r, 
        label: r, 
        minWidth: 200,
        numeric: typeof dataProps[0][r] === 'number'
    }))

    type Data = {
        [key: string]: string | number
    }

    const rows: Data[] = dataProps // .map((r:any) => transform(r))

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const row_data = rows.map(r => ({ x: r[axes.x], y: r[axes.y] }))

    // Sort the data based on the current sort parameters
    const sortedRows = useMemo(() => {
        if (!orderBy) return rows;
        
        return [...rows].sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            
            if (aValue === bValue) return 0;
            
            const comparison = aValue < bValue ? -1 : 1;
            return order === 'asc' ? comparison : -comparison;
        });
    }, [rows, order, orderBy]);

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableHeader displayType={displayType}
                fields={columnNames}
                setDisplayType={setDisplayType}
                showGraphIcon={(rows.length > 1)}
                setAxes={setAxes} />

            { displayType === 'chart' && <ParetoChart xTitle={axes.x} yTitle={axes.y} data={row_data} />}
            
            {displayType == 'table' && (<><TableContainer sx={{ maxHeight: 440 }}>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                        <TableRow>
                            {columns.map((column, index) => (
                                <TableCell
                                    key={index}
                                    align={column.align}
                                    style={{ minWidth: column.minWidth, fontWeight: 'bold' }}
                                    sortDirection={orderBy === column.id ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === column.id}
                                        direction={orderBy === column.id ? order : 'asc'}
                                        onClick={() => handleRequestSort(column.id)}
                                    >
                                        {column.label.split('_').join(' ').toUpperCase()}
                                        {orderBy === column.id ? (
                                            <Box component="span" sx={visuallyHidden}>
                                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                            </Box>
                                        ) : null}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedRows
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, cindex) => {
                                return (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={cindex}>
                                        {columns.map((column, kindex) => {
                                            const k = column.id
                                            
                                            const val = !column.label.includes('_lat') && !column.label.includes('_lon') && isFloat(row[k]) ? parseFloat(row[k].toString()).toFixed(2) : row[k]

                                            return (
                                                <TableCell key={kindex} align={column.align}>
                                                    {column.format ? column.format(row[k]) : val}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
            
            <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </>)}
        </Paper>
    );
}

export default TabularData
